import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Space, 
  message, 
  Checkbox,
  List,
  Tag,
  Modal,
  Alert,
  Spin
} from 'antd';
import { 
  SendOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  CrownOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface UserWithEmail {
  user_id: string;
  username: string;
  email: string;
  role: string;
}

const EmailSender: React.FC = () => {
  const [form] = Form.useForm();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [usersWithEmail, setUsersWithEmail] = useState<UserWithEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);

  // 이메일이 있는 사용자 목록 조회
  const fetchUsersWithEmail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userService.getUsersWithEmail();
      if (response.success && response.data) {
        setUsersWithEmail(response.data);
      } else {
        message.error(response.message || t('emailSender.loadFailed'));
      }
    } catch (error: any) {
      message.error(t('emailSender.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsersWithEmail();
    // 폼 초기값 설정
    form.setFieldsValue({
      fromName: '교화단관리본부'
    });
  }, [fetchUsersWithEmail, form]);

  // 역할별 전체 선택/해제
  const handleSelectAllByRole = (role: string, checked: boolean) => {
    const usersByRole = usersWithEmail.filter(user => user.role === role);
    const emailsByRole = usersByRole.map(user => user.email);
    
    if (checked) {
      // 해당 역할의 모든 사용자 추가 (중복 제거)
      setSelectedUsers(prev => {
        const newSelection = [...prev, ...emailsByRole];
        return Array.from(new Set(newSelection));
      });
    } else {
      // 해당 역할의 모든 사용자 제거
      setSelectedUsers(prev => prev.filter(email => !emailsByRole.includes(email)));
    }
  };

  // 개별 사용자 선택/해제
  const handleUserSelect = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, email]);
    } else {
      setSelectedUsers(prev => prev.filter(e => e !== email));
    }
  };

  // 메일 발송
  const handleSendEmail = async (values: any) => {
    if (selectedUsers.length === 0) {
      message.warning(t('emailSender.selectRecipients'));
      return;
    }

    setSending(true);
    try {
      console.log('메일 발송 요청:', {
        toEmails: selectedUsers,
        subject: values.subject,
        content: values.content,
        fromName: values.fromName,
      });

      const response = await userService.sendBulkEmail({
        toEmails: selectedUsers,
        subject: values.subject,
        content: values.content,
        fromName: values.fromName,
      });

      console.log('메일 발송 응답:', response);

      if (response.success) {
        setSendResult(response.result);
        setResultModalVisible(true);
        
        // 성공/실패 여부에 따라 다른 메시지 표시
        if (response.result && response.result.success.length > 0 && response.result.failed.length === 0) {
          message.success(`메일 발송이 완료되었습니다. (성공: ${response.result.success.length}건)`);
        } else if (response.result && response.result.success.length > 0 && response.result.failed.length > 0) {
          message.warning(`메일 발송이 완료되었습니다. (성공: ${response.result.success.length}건, 실패: ${response.result.failed.length}건)`);
        } else if (response.result && response.result.failed.length > 0) {
          message.error(`메일 발송에 실패했습니다. (실패: ${response.result.failed.length}건)`);
        } else {
          message.success(response.message || '메일 발송이 완료되었습니다.');
        }
        
        form.resetFields();
        setSelectedUsers([]);
      } else {
        console.error('메일 발송 실패:', response);
        message.error(response.message || t('emailSender.sendFailed'));
      }
    } catch (error: any) {
      console.error('메일 발송 오류:', error);
      
      // 타임아웃 오류 처리
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message.error(t('emailSender.timeoutError'));
      } else if (error.response?.status === 401) {
        message.error(t('emailSender.authExpired'));
      } else if (error.response?.status === 403) {
        message.error(t('emailSender.noPermission'));
      } else {
        message.error(t('emailSender.sendFailed'));
      }
    } finally {
      setSending(false);
    }
  };

  // 권한 확인
  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('common.noPermission')}</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('emailSender.title')}
          </Title>
          <Space>
            <Button 
              icon={<MailOutlined />}
              onClick={() => navigate('/admin/email-logs')}
            >
              발송내역
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchUsersWithEmail}
              loading={loading}
            >
              {t('emailSender.refresh')}
            </Button>
          </Space>
        </div>

        <Alert
          message={t('emailSender.info')}
          description={t('emailSender.description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ display: 'flex', gap: '24px' }}>
          {/* 수신자 선택 */}
          <div style={{ flex: 1 }}>
            <Card 
              title={t('emailSender.recipients')} 
              size="small"
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : (
                <div>
                  {/* 관리자 섹션 */}
                  {usersWithEmail.filter(user => user.role === 'ADMIN').length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fff2e8',
                        borderRadius: '4px',
                        border: '1px solid #ffd591'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CrownOutlined style={{ color: '#fa8c16' }} />
                          <Text strong style={{ color: '#d46b08' }}>
                            {t('emailSender.adminUsers')} ({usersWithEmail.filter(user => user.role === 'ADMIN').length}명)
                          </Text>
                        </div>
                        <Checkbox
                          checked={usersWithEmail.filter(user => user.role === 'ADMIN').every(user => selectedUsers.includes(user.email)) && usersWithEmail.filter(user => user.role === 'ADMIN').length > 0}
                          indeterminate={usersWithEmail.filter(user => user.role === 'ADMIN').some(user => selectedUsers.includes(user.email)) && !usersWithEmail.filter(user => user.role === 'ADMIN').every(user => selectedUsers.includes(user.email))}
                          onChange={(e) => handleSelectAllByRole('ADMIN', e.target.checked)}
                        >
                          {t('emailSender.selectAll')}
                        </Checkbox>
                      </div>
                      <List
                        size="small"
                        dataSource={usersWithEmail.filter(user => user.role === 'ADMIN')}
                        renderItem={(user) => (
                          <List.Item style={{ padding: '8px 0' }}>
                            <Checkbox
                              checked={selectedUsers.includes(user.email)}
                              onChange={(e) => handleUserSelect(user.email, e.target.checked)}
                            >
                              <Space>
                                <CrownOutlined style={{ color: '#fa8c16' }} />
                                <Text strong>{user.username}</Text>
                                <Text type="secondary">({user.email})</Text>
                                <Tag color="red">{t('navigation.admin')}</Tag>
                              </Space>
                            </Checkbox>
                          </List.Item>
                        )}
                      />
                    </div>
                  )}

                  {/* 일반 사용자 섹션 */}
                  {usersWithEmail.filter(user => user.role === 'GENERAL').length > 0 && (
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#f6ffed',
                        borderRadius: '4px',
                        border: '1px solid #b7eb8f'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <UserOutlined style={{ color: '#52c41a' }} />
                          <Text strong style={{ color: '#389e0d' }}>
                            {t('emailSender.generalUsers')} ({usersWithEmail.filter(user => user.role === 'GENERAL').length}명)
                          </Text>
                        </div>
                        <Checkbox
                          checked={usersWithEmail.filter(user => user.role === 'GENERAL').every(user => selectedUsers.includes(user.email)) && usersWithEmail.filter(user => user.role === 'GENERAL').length > 0}
                          indeterminate={usersWithEmail.filter(user => user.role === 'GENERAL').some(user => selectedUsers.includes(user.email)) && !usersWithEmail.filter(user => user.role === 'GENERAL').every(user => selectedUsers.includes(user.email))}
                          onChange={(e) => handleSelectAllByRole('GENERAL', e.target.checked)}
                        >
                          {t('emailSender.selectAll')}
                        </Checkbox>
                      </div>
                      <List
                        size="small"
                        dataSource={usersWithEmail.filter(user => user.role === 'GENERAL')}
                        renderItem={(user) => (
                          <List.Item style={{ padding: '8px 0' }}>
                            <Checkbox
                              checked={selectedUsers.includes(user.email)}
                              onChange={(e) => handleUserSelect(user.email, e.target.checked)}
                            >
                              <Space>
                                <UserOutlined style={{ color: '#52c41a' }} />
                                <Text strong>{user.username}</Text>
                                <Text type="secondary">({user.email})</Text>
                              </Space>
                            </Checkbox>
                          </List.Item>
                        )}
                      />
                    </div>
                  )}

                  {/* 사용자가 없는 경우 */}
                  {usersWithEmail.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#999', 
                      padding: '20px 0' 
                    }}>
                      {t('emailSender.noUsers')}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* 메일 작성 */}
          <div style={{ flex: 1 }}>
            <Card title={t('emailSender.compose')} size="small">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSendEmail}
              >
                <Form.Item
                  name="fromName"
                  label={t('emailSender.fromName')}
                >
                  <Input placeholder={t('emailSender.fromNamePlaceholder')} />
                </Form.Item>

                <Form.Item
                  name="subject"
                  label={t('emailSender.subject')}
                  rules={[{ required: true, message: t('emailSender.subjectRequired') }]}
                >
                  <Input placeholder={t('emailSender.subjectPlaceholder')} />
                </Form.Item>

                <Form.Item
                  name="content"
                  label={t('emailSender.content')}
                  rules={[{ required: true, message: t('emailSender.contentRequired') }]}
                >
                  <TextArea 
                    rows={8} 
                    placeholder={t('emailSender.contentPlaceholder')}
                    showCount
                    maxLength={2000}
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button 
                      type="primary" 
                      icon={<SendOutlined />}
                      onClick={() => form.submit()}
                      loading={sending}
                      disabled={selectedUsers.length === 0}
                    >
                      {t('emailSender.send')}
                    </Button>
                    <Text type="secondary">
                      {t('emailSender.selectedCount', { count: selectedUsers.length })}
                    </Text>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </Card>

      {/* 발송 결과 모달 */}
      <Modal
        title={t('emailSender.resultTitle')}
        open={resultModalVisible}
        onCancel={() => setResultModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setResultModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
        width={600}
      >
        {sendResult && (
          <div>
            <Alert
              message={t('emailSender.sendComplete')}
              description={t('emailSender.sendResult', { 
                success: sendResult.success.length, 
                failed: sendResult.failed.length 
              })}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {sendResult.success.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>{t('emailSender.successList')}:</Text>
                <List
                  size="small"
                  dataSource={sendResult.success}
                  renderItem={(email: string) => (
                    <List.Item>
                      <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text>{email}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}

            {sendResult.failed.length > 0 && (
              <div>
                <Text strong>{t('emailSender.failedList')}:</Text>
                <List
                  size="small"
                  dataSource={sendResult.failed}
                  renderItem={(email: string) => (
                    <List.Item>
                      <Space>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        <Text>{email}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailSender; 